// axiosInstance, not the cachios client — see enrollments.js
import {axiosInstance as http} from "@/http_client";
import * as pp from "@/utils/paymentPlans";

/**
 * Account-level billing state shared by the dashboard banners, the
 * dashboard's payment-plan panel and the Account page: payment plans, the
 * rep-prepared cart quote, and the expired-course summary. One source so the
 * banners and the panels can never disagree.
 *
 * All loads pass {force: true} — the cachios client would otherwise serve a
 * 2-minute-stale copy right after the student comes back from signing at TPO.
 */
export default {
    state: {
        paymentPlans: null,
        cartQuotes: null,
        expiredCourses: null,
    },
    getters: {
        paymentPlans: state => state.paymentPlans || [],
        paymentPlansLoaded: state => state.paymentPlans !== null,
        cartQuotes: state => state.cartQuotes || [],
        expiredCourses: state => state.expiredCourses || {hasExpired: false, count: 0, inGracePeriod: false, gracePeriodCount: 0},

        needsOnboarding: (state, getters) => pp.needsOnboarding(getters.paymentPlans),
        needsDocuSign: (state, getters) => pp.needsDocuSign(getters.paymentPlans),
        tpoOnboardingUrl: (state, getters) => pp.tpoOnboardingUrl(getters.paymentPlans),
        tpoPendingAgreementUrl: (state, getters) => pp.tpoPendingAgreementUrl(getters.paymentPlans),
        hasUnpaidSalesTax: (state, getters) => pp.hasUnpaidSalesTax(getters.paymentPlans),
        needsPaymentMethod: (state, getters) => pp.needsPaymentMethod(getters.paymentPlans),
        hasCartEnrollment: (state, getters) => getters.cartQuotes.length > 0,
        hasExpiredCourses: (state, getters) => getters.expiredCourses.hasExpired && getters.expiredCourses.count > 0,
    },
    mutations: {
        setPaymentPlans(state, plans) {
            state.paymentPlans = Array.isArray(plans) ? plans : [];
        },
        setCartQuotes(state, quotes) {
            state.cartQuotes = Array.isArray(quotes) ? quotes : [];
        },
        setExpiredCourses(state, summary) {
            state.expiredCourses = summary || null;
        },
    },
    actions: {
        // Each load remembers who it was for and drops its response if the
        // user changed meanwhile (portal login-as-user relay) — see
        // enrollments.js for the race this guards against
        getPaymentPlans({commit, rootGetters}) {
            const forUser = rootGetters.currentUser && rootGetters.currentUser.id;
            return http.get('app/users/payment-plans').then(response => {
                if ((rootGetters.currentUser && rootGetters.currentUser.id) !== forUser) return null;
                commit('setPaymentPlans', response.data);
                return response.data;
            }).catch(err => {
                console.error('Error loading payment plans:', err);
                throw err;
            });
        },
        getCartQuotes({commit, rootGetters}) {
            const forUser = rootGetters.currentUser && rootGetters.currentUser.id;
            return http.get('app/users/cart-quotes').then(response => {
                if ((rootGetters.currentUser && rootGetters.currentUser.id) !== forUser) return null;
                commit('setCartQuotes', response.data);
                return response.data;
            }).catch(err => {
                console.error('Error loading cart quotes:', err);
                throw err;
            });
        },
        getExpiredCourses({commit, rootGetters}) {
            const forUser = rootGetters.currentUser && rootGetters.currentUser.id;
            return http.get('app/users/expired-courses').then(response => {
                if ((rootGetters.currentUser && rootGetters.currentUser.id) !== forUser) return null;
                commit('setExpiredCourses', response.data);
                return response.data;
            }).catch(err => {
                console.error('Error loading expired courses:', err);
                throw err;
            });
        },
        // Everything the dashboard banners need, fetched together; failures
        // are logged per call and never block the others
        getBillingState({dispatch}) {
            return Promise.allSettled([
                dispatch('getPaymentPlans'),
                dispatch('getCartQuotes'),
                dispatch('getExpiredCourses'),
            ]);
        },
    }
};
